import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, IconButton, Dialog, DialogContent, DialogTitle, TextField
} from '@mui/material';
import { format } from 'date-fns';
import {BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import SVGBDComponent from './img/bloodimg';
import SVGComponent from './img/syringeimg';
import SVGTKComponent from './img/tickimg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import { apiHandler } from '../../../services/authService';


interface ImmunizationData {
  id: number;
  name: string;
  slug: string;
  attributes: {
    values: {
      periods: string;
      treatment: string;
      status: boolean;
      freeze: number;
      imm_id: number | null;
      taken_at: string | null;
      dosage_date: string | null;
    }[];
  };
}

interface ImmuneFormProps {
  patientId: string;
}

const ImmunizationTable: React.FC<ImmuneFormProps> = ({ patientId }) => {
  const [immunizations, setImmunizations] = useState<ImmunizationData[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedImmunization, setSelectedImmunization] = useState<ImmunizationData | null>(null);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState<number | null>(null);
  const [status, setStatus] = useState(false);
  const [takenAt, setTakenAt] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const apiUrl = process.env.BASE_URL;
 

  const fetchImmunizations = async () => {
    setLoading(true); // Step 2: Set loading state to true
    const url = `${BASE_URL}/resource/masters/all?slug=immunisation&order_by=id&dir=1&patient_id=${patientId}`;

    await apiHandler('', 'GET', {}, url)
    .then(response => {
      if (!response) {
        throw new Error("No response received from the server");
      }
      const data = response.data;
      if (data && data.data && data.data.masters) {
        setImmunizations(data.data.masters);
      }
      setLoading(false); // Step 2: Set loading state to false
    })
    .catch(error => {
      console.error('Error fetching immunization data:', error);
      setLoading(false); // Step 2: Ensure loading state is false on error
    });

    // fetch(`${BASE_URL}/resource/masters/all?slug=immunisation&order_by=id&dir=1&patient_id=${patientId}`, {
    //   headers: getAxiosConfig().headers
    // })
    //   .then(response => response.json())
    //   .then(data => {
    //     setImmunizations(data.data.masters);
    //     setLoading(false); // Step 2: Set loading state to false
    //   })
    //   .catch(error => {
    //     console.error('Error fetching immunization data:', error);
    //     setLoading(false); // Step 2: Ensure loading state is false on error
    //   });
  };

  useEffect(() => {
    fetchImmunizations();
  }, [patientId]);

  const formatDate = (dateString: string | null) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      return '';
    }
    return format(new Date(dateString), 'dd-MM-yyyy');
  };

  const renderTreatmentIcon = (treatment: string, takenAt: string | null) => {
    let icon: React.ReactNode = null;

    switch (treatment) {
      case 'drop':
        icon = <SVGBDComponent width={30} height={30} />;
        break;
      case 'dose':
        icon = <SVGComponent width={30} height={30} />;
        break;
      default:
        icon = null;
    }

    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </div>
        {takenAt && (
          <SVGTKComponent
            style={{
              position: 'absolute',
              bottom: 23,
              left: 25,
              width: 25,
              height: 25,
              pointerEvents: 'none'
            }}
          />
        )}
      </div>
    );
  };

  const handleDialogOpen = (immunization: ImmunizationData, index: number) => {
    setSelectedImmunization(immunization);
    setSelectedPeriodIndex(index);
    setStatus(immunization.attributes.values[index].status);
    
    // Set `takenAt` to the current date if it is not already set
    const currentDate = new Date().toISOString().split('T')[0]; // Formats to YYYY-MM-DD
    setTakenAt(immunization.attributes.values[index].taken_at || currentDate);
  
    setValidationError(null); // Reset validation error
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setStatus(false);
    setValidationError(null); // Reset validation error
  };

  const handleDeleteDialogOpen = (immunization: ImmunizationData, index: number) => {
    setSelectedImmunization(immunization);
    setSelectedPeriodIndex(index);
    setOpenDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
    setSelectedImmunization(null);
  };

 

  
  return (
    <>
     {loading ? ( // Step 3: Render loading indicator
        <div className="flex justify-center items-center" style={{ height: '100%' }}>
          <LoadingAnimation />
        </div>
      ) : (
      <TableContainer component={Paper} style={{ maxHeight: '1000px', overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {['S.No', 'Vaccine', 'Period', 'Status', 'Dosage Date', 'Taken At'].map((header) => (
                <TableCell key={header}  className="px-4 py-1 font-jost" // Apply Jost font here
                sx={{ color: '#FFFFFF', background: '#1a1a1a', fontFamily: 'Jost, sans-serif', fontWeight: 'bold' }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className="font-jost">
            {immunizations.map((immunization, index) =>
              immunization.attributes.values.map((value, i) => (
                <TableRow className="font-jost" key={`${immunization.id}-${i}`} sx={{ borderBottom: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>
                  {i === 0 && (
                    <>
                      <TableCell rowSpan={immunization.attributes.values.length} sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>
                        {index + 1}
                      </TableCell>
                      <TableCell rowSpan={immunization.attributes.values.length} sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>
                        <Typography className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' }}>{immunization.name}</Typography>
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>{value.periods}</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>
                    <IconButton
                      onClick={() => value.taken_at ? handleDeleteDialogOpen(immunization, i) : handleDialogOpen(immunization, i)}
                    >
                      {renderTreatmentIcon(value.treatment, value.taken_at)}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>{formatDate(value.dosage_date)}</TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ddd', fontFamily: 'Jost, sans-serif' }}>{value.taken_at ? formatDate(value.taken_at) : ''}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      

      
    </>
  );
};

export default ImmunizationTable;
